import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../SEO';
import { appConfig } from '../../config/appConfig';
import machiningHeroVisual from '../../images/pexels-gdtography-277628-911738.jpg';
import throughputVisual from '../../images/Comp_2.gif';
import backlogVisual from '../../images/two.gif';

const faqs = [
  {
    question: 'What EBITDA multiples are precision machining shops receiving?',
    answer:
      'Tightly run precision machining companies with AS9100 or ISO certifications, 30%+ gross margins, and diversified end markets frequently command 6-9x EBITDA. Highly specialized aerospace or defense suppliers with sole-source status can exceed 10x when backlog visibility is strong.',
  },
  {
    question: 'How critical is customer concentration when selling a machining business?',
    answer:
      'Buyers underwrite concentration carefully. If your top customer exceeds 30% of revenue, we demonstrate contractual visibility, switching costs, and second-source mitigation plans. We also surface backlog, forecast accuracy, and engineering collaboration to reduce perceived risk.',
  },
  {
    question: 'What diligence materials do manufacturing buyers expect?',
    answer:
      'Expect to deliver detailed capacity models, machine lists with vintages, OEE dashboards, quality escapes, corrective action logs, and compliance records. We build a readiness room that addresses these in advance so diligence stays on offense.',
  },
  {
    question: 'Do I need to stay with the company after a sale?',
    answer:
      'Most buyers expect leadership continuity for 12-24 months. We negotiate transition incentives, carve-outs for the next generation of managers, and post-close roles that preserve work-life balance while keeping the premium multiple intact.',
  },
];

const structuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Manufacturing & Precision Machining M&A Advisory',
    'name': 'Manufacturing & Precision Machining M&A',
    'provider': {
      '@type': 'Organization',
      'name': appConfig.site.organizationName,
      'url': appConfig.site.baseUrl,
    },
    'areaServed': ['United States', 'Canada', 'Mexico'],
    'audience': {
      '@type': 'Audience',
      'name': 'Owners of precision machining and industrial manufacturing companies',
    },
    'url': `${appConfig.site.baseUrl}/manufacturing-precision-machining-mna`,
    'description':
      'Sell-side advisory for machining and industrial services companies. AMIVA positions certifications, backlog, capacity, and utilization to secure premium outcomes.',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock',
      'url': `${appConfig.site.baseUrl}/valuation`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  },
];

const heroMetrics = [
  {
    label: 'Certified Buyer Grid',
    value: '160+',
    description: 'strategic and private equity buyers with AS9100 / ISO mandates engaged by AMIVA.',
  },
  {
    label: 'Valuation Lift',
    value: '28%',
    description: 'average premium unlocked by showcasing backlog, tolerances, and utilization data.',
  },
  {
    label: 'Data Room Sprint',
    value: '30 days',
    description: 'time to build QOE-ready machine matrices, compliance logs, and throughput analytics.',
  },
];

const sectorNuances = [
  'Certification stack (AS9100, ISO 13485, ITAR) that opens regulated sectors.',
  'Tolerances, materials specialization, and engineering complexity of parts produced.',
  'Backlog visibility, program lifecycles, and contract clauses governing pricing escalators.',
  'Operator skill depth, apprenticeship programs, and tribal knowledge documentation.',
  'Capital expenditure cadence with machine repair logs and preventive maintenance discipline.',
];

const valuationDrivers = [
  'Revenue mix by end market (aerospace, medical device, energy, industrial automation).',
  'Gross margin trends tied to throughput, scrap, rework, and lean initiatives.',
  'Capacity utilization, OEE, and machine uptime across shifts and cells.',
  'Tooling changeover efficiency, setup reduction programs, and automation footprint.',
  'Working capital discipline covering inventory turns, WIP aging, and supplier terms.',
];

const buyerTypes = [
  'Strategic acquirers consolidating regional capacity to win larger OEM programs.',
  'Private equity platforms executing buy-and-build strategies across precision machining.',
  'Tier-one suppliers looking for second-source partners to de-risk production.',
  'Industrial conglomerates expanding into high-margin prototyping and low-volume runs.',
  'ESOP transitions that keep control in-house while creating liquidity for retiring owners.',
];

const prepChecklist = [
  'Complete a machine-by-machine capability matrix with tolerances, software, and utilization.',
  'Reconcile backlog to purchase orders and forecast labor hours by program.',
  'Document quality metrics: PPM trends, corrective action closure rates, and audit results.',
  'Refresh OSHA, ITAR, and environmental compliance files with expiration tracking.',
  'Summarize pricing adjustments, surcharges, and raw material pass-through mechanics.',
  'Produce retention plans for supervisors, programmers, and lead machinists.',
  'Map layout efficiency, planned capital projects, and automation roadmap.',
  'Highlight strategic supplier agreements, dual-source strategies, and long-lead inventory.',
];

const listItemClasses =
  'flex items-start gap-3 text-sm sm:text-base leading-relaxed text-gray-700';
const listDotClasses =
  'mt-1.5 h-2 w-2 flex-none rounded-full bg-amiva-primary';

const visualShowcase = [
  {
    image: machiningHeroVisual,
    alt: 'Precision machining floor with operators and CNC equipment.',
    label: 'Operational Tour',
    metric: 'AS9100 spotlight',
    caption: 'Virtual walkthroughs highlight 5-axis cells, certifications, and automation footprint.',
    gradient: 'from-[#0B192E]/70 via-[#1E3A8A]/60 to-[#10B981]/60',
  },
  {
    image: throughputVisual,
    alt: 'Throughput heatmap visualizing utilization and scrap.',
    label: 'Throughput Heatmap',
    metric: '28% EBITDA lift',
    caption: 'OEE stories capture scrap reduction and setup discipline that buyers can diligence fast.',
    gradient: 'from-[#12274A]/70 via-[#1B3A70]/60 to-[#F97316]/60',
  },
  {
    image: backlogVisual,
    alt: 'Backlog dashboard charting programs and revenue visibility.',
    label: 'Backlog Visibility',
    metric: '30 day prep sprint',
    caption: 'Program forecasts and backlog segmentation mitigate customer concentration concerns.',
    gradient: 'from-[#0F172A]/70 via-[#2563EB]/60 to-[#9333EA]/60',
  },
];

const highlightStats = [
  {
    label: 'Cash at Close',
    value: '75%',
    detail: 'median upfront proceeds delivered to owners while rolling selective equity for upside.',
  },
  {
    label: 'Buyer Engagement Depth',
    value: '3.4x',
    detail: 'increase in diligence interactions once machine matrices and OEE heatmaps go live.',
  },
  {
    label: 'Closing Timeline',
    value: '12-18 weeks',
    detail: 'from competitive LOI to funds-flow when AMIVA orchestrates diligence choreography.',
  },
];

const capabilitySnapshots = [
  {
    title: 'Machine Capability Matrix',
    description: 'Interactive grid charting tolerances, materials, and CNC investments by cell.',
    icon: (
      <svg className="w-10 h-10 text-amiva-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 9h8M8 13h5" />
      </svg>
    ),
  },
  {
    title: 'Throughput Heatmaps',
    description: 'Shift-by-shift visuals tracking OEE, scrap, and rework across machines.',
    icon: (
      <svg className="w-10 h-10 text-amiva-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4 4 4 4-4 4 4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16" />
      </svg>
    ),
  },
  {
    title: 'Compliance Command Center',
    description: 'Dashboard snapshots for AS9100, ISO 13485, ITAR, OSHA, and environmental logs.',
    icon: (
      <svg className="w-10 h-10 text-amiva-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 10l6-4 6 4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 14l6 4 6-4" />
      </svg>
    ),
  },
];

const processHighlights = [
  {
    title: 'Operational Baseline',
    timeline: 'Weeks 1-2',
    items: ['Machine matrix audit', 'Backlog + program mapping', 'Compliance evidence binder'],
  },
  {
    title: 'Buyer Spotlight',
    timeline: 'Weeks 3-5',
    items: ['Video walkthrough narrative', 'Tiered NDA releases', 'Utilization + margin dashboards'],
  },
  {
    title: 'Commitment Lock',
    timeline: 'Weeks 6-10',
    items: ['Cash vs. capex blending', 'Management retention plans', 'Synergy validation workshops'],
  },
];

const personaProfiles = [
  {
    title: 'Defense & Aerospace Suppliers',
    stat: 'AS9100 • ITAR',
    description:
      'Sole-source or top-tier suppliers supporting defense primes who need to demonstrate compliance, program visibility, and surge capacity without compromising security.',
  },
  {
    title: 'Medical Device Contract Manufacturers',
    stat: 'ISO 13485 • Clean Rooms',
    description:
      'Lights-out machining and sterile assembly teams balancing FDA audits, traceability, and rapid prototyping while pursuing strategic or PE partnerships.',
  },
  {
    title: 'Industrial Automation & Aftermarket Leaders',
    stat: 'Controls + Service Mix',
    description:
      'Shops blending precision machining with controls, field service, and aftermarket revenue seeking buyers that value recurring programs and engineering depth.',
  },
];

const relatedPlaybooks = [
  {
    title: 'Cross-Border M&A Advisory',
    description: 'Coordinate US and European buyers when your programs span multinational OEMs.',
    to: '/cross-border-mna-us-europe',
  },
  {
    title: 'SaaS & Software M&A Advisor',
    description: 'Pair your Industry 4.0 software stack with premium narratives for strategic platforms.',
    to: '/saas-mna-advisor',
  },
  {
    title: 'HVAC & Home Services M&A Advisor',
    description: 'Service-heavy industrial operators expanding into field and aftermarket solutions.',
    to: '/hvac-home-services-mna-advisor',
  },
];

export default function ManufacturingPrecisionMachining() {
  return (
    <div className="bg-white">
      <SEOHead
        title="Manufacturing & Precision Machining M&A | AMIVA Equity Partners"
        description="Sell-side for machining and industrial services. We translate certifications, tolerances, and backlog into premium valuations and disciplined buyer processes."
        keywords={[
          'precision machining M&A',
          'manufacturing sell-side advisor',
          'sell machine shop',
          'industrial services investment bank',
        ]}
        canonical="/manufacturing-precision-machining-mna"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
          { name: 'Manufacturing & Precision Machining', path: '/manufacturing-precision-machining-mna' },
        ]}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[-6rem] -left-24 w-[28rem] h-[28rem] bg-amiva-primary/45 rounded-full blur-3xl"></div>
          <div className="absolute bottom-[-6rem] right-[-4rem] w-[30rem] h-[30rem] bg-amiva-accent/30 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,_3fr)_minmax(0,_2.4fr)] gap-12 items-center">
            <div className="max-w-3xl">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-6">
                Manufacturing &amp; Precision Machining M&amp;A
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
                Showcase tolerances, backlog, and team depth in minutes
              </h1>
              <p className="text-base sm:text-lg text-gray-200 leading-relaxed mb-6">
                We convert machine matrices, compliance logs, and customer scorecards into visuals
                buyers trust. Strategic acquirers and PE platforms get the diligence inputs they
                need—while operators stay on the floor running the business.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {heroMetrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-2xl border border-white/15 bg-white/5 p-4 text-center"
                  >
                    <div className="text-2xl font-semibold text-white">{metric.value}</div>
                    <p className="mt-1 text-xs uppercase tracking-[0.28em] text-white/70">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-white/60">
                      {metric.description}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Link to="/valuation" className="btn-primary px-8 py-4 text-lg font-semibold">
                  Request a Shop Valuation
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-base font-semibold text-white hover:text-amiva-accent transition-colors"
                >
                  Speak with a manufacturing banker →
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-10 right-0 h-24 w-24 rounded-full bg-amiva-accent/40 blur-xl"></div>
              <div className="absolute bottom-[-2rem] -left-10 h-20 w-20 rounded-full bg-white/20 blur-lg"></div>
              <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-[0_30px_60px_rgba(11,25,46,0.45)]">
                <img
                  src={machiningHeroVisual}
                  alt="Precision machining production line with CNC equipment."
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#12274A]/35 via-[#1B3A70]/10 to-transparent"></div>
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-xs uppercase tracking-[0.32em] text-white">
                  Certified capacity
                </div>
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                  <p className="text-sm font-semibold text-white">
                    5-axis uptime 92% • <span className="text-white/80">scrap rate ↓ 140 bps</span>
                  </p>
                  <p className="mt-1 text-xs text-white/70">
                    Visual readiness packets validate throughput and compliance before first plant tour.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Founder Profiles */}
      <section className="py-16 lg:py-20 bg-gray-50" id="founder-profiles">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Founder Snapshot
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Operators we position for premium industrial and machining exits
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Every mandate begins with the program mix, compliance posture, and leadership bench.
              These archetypes highlight how we tailor buyer targeting, rollover decisions, and
              post-close roles.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {personaProfiles.map((profile) => (
              <div
                key={profile.title}
                className="rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  {profile.stat}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{profile.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{profile.description}</p>
                <div className="text-xs text-gray-500">
                  We choreograph management retention, earn-outs, and capex commitments before exclusivity.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Shop Storyboard
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Visual collateral that replaces plant tours until buyers earn access
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Founders get a camera-ready playbook: operational walkthroughs, throughput heatmaps,
              and backlog segmentation that keep diligence efficient without giving away IP early.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visualShowcase.map((shot) => (
              <div
                key={shot.label}
                className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-gray-900/5 shadow-lg"
              >
                <img
                  src={shot.image}
                  alt={shot.alt}
                  className="h-60 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${shot.gradient} opacity-80 mix-blend-multiply`}
                ></div>
                <div className="absolute inset-0 flex flex-col justify-between p-6">
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/15 px-3 py-1 text-xs uppercase tracking-[0.28em] text-white">
                    {shot.label}
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                    <div className="text-sm font-semibold text-white">{shot.metric}</div>
                    <p className="mt-1 text-xs text-white/75 leading-relaxed">{shot.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-gradient p-8 md:p-12 rounded-3xl border border-gray-100 shadow-xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-10">
              <div>
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-2">
                  Precision machining insights
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Proof points institutional buyers ask for in diligence
                </h2>
              </div>
              <p className="text-sm md:text-base text-gray-600 max-w-xl">
                We quantify certifications, throughput, and backlog visibility the way strategic
                acquirers and PE platforms underwrite deals—so diligence starts on your terms.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {highlightStats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-transform duration-300 hover:-translate-y-1 flex flex-col gap-3 h-full"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amiva-primary">
                    {stat.label}
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
                Shop floor story
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Sector Nuances That Command Attention
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                We translate capability depth into visuals: which cells run lights-out, which
                programs drive backlog, where compliance investments de-risk customers.
              </p>
              <div className="card-premium p-6 border border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {sectorNuances.slice(0, 4).map((item) => (
                    <div key={item} className={listItemClasses}>
                      <span className={listDotClasses}></span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {capabilitySnapshots.map((snapshot) => (
                <div key={snapshot.title} className="card-premium p-6 border border-gray-100 flex flex-col gap-4">
                  <div className="flex items-center justify-center w-14 h-14 bg-amiva-gradient-subtle rounded-2xl">
                    {snapshot.icon}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">{snapshot.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{snapshot.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 text-white">Valuation Driver Dashboard</h2>
            <p className="text-base text-white/70">
              Visual metrics buyers scrutinize instantly
            </p>
          </div>
          
          {/* Visual Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { icon: '⚙️', label: 'OEE & Uptime', value: 'Efficiency', sublabel: 'Overall equipment effectiveness shows operational discipline', color: 'blue' },
              { icon: '📦', label: 'Backlog Coverage', value: 'Visibility', sublabel: 'Months of firm orders tied to purchase orders', color: 'green' },
              { icon: '🎯', label: 'On-Time Delivery', value: 'Reliability', sublabel: 'Customer scorecards prove program execution', color: 'purple' },
              { icon: '✓', label: 'AS9100/ISO Certified', value: 'Market Access', sublabel: 'Aerospace, medical, defense certifications required', color: 'indigo' },
              { icon: '📊', label: 'Capacity Utilization', value: 'Scalability', sublabel: 'Shift coverage and machine availability by cell', color: 'yellow' },
              { icon: '🔧', label: 'Scrap & Rework Rate', value: 'Quality', sublabel: 'First-pass yield impacts margin sustainability', color: 'red' },
              { icon: '👥', label: 'Customer Concentration', value: 'Risk Profile', sublabel: 'Top 3 customer exposure and contract terms', color: 'pink' },
              { icon: '💰', label: 'Gross Margin by Program', value: 'Mix Quality', sublabel: 'Prototyping vs production vs blanket orders', color: 'green' },
              { icon: '⚡', label: 'Setup & Changeover Time', value: 'Flexibility', sublabel: 'Quick-change tooling enables smaller lots', color: 'orange' },
              { icon: '🏭', label: 'Machine Capability Matrix', value: 'Asset Depth', sublabel: 'Tolerances, materials, software by machine', color: 'gray' }
            ].map((metric, idx) => (
              <div key={idx} className={`relative bg-gradient-to-br from-slate-900/40 to-slate-800/40 backdrop-blur rounded-2xl border-2 border-slate-400/30 p-5 hover:border-slate-400 transition-all duration-300 hover:-translate-y-1`}>
                <div className="text-4xl mb-3 text-center">{metric.icon}</div>
                <div className="text-sm font-bold text-white mb-2 text-center">{metric.label}</div>
                <div className="text-xs font-semibold text-white/90 uppercase tracking-wider mb-2 text-center">{metric.value}</div>
                <div className="text-xs text-white/70 leading-snug">{metric.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
                Buyer alignment
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Buyer Types We Activate</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {processHighlights.map((phase) => (
                  <div key={phase.title} className="card-premium p-6 border border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-3">
                      {phase.timeline}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amiva-primary"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-gradient p-8 shadow-xl transition-all duration-300 hover:-translate-y-1">
              <ul className="space-y-3">
                {buyerTypes.map((type) => (
                  <li key={type} className={listItemClasses}>
                    <span className={listDotClasses}></span>
                    <span>{type}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                Scorecards track backlog appetite, capacity gaps, and integration style so you only meet buyers who can truly scale your operation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Exit Prep: 8 Sprint Tracks
            </h2>
            <p className="text-base text-gray-600">
              Visual workstream board with weekly milestones
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { num: '1', icon: '🏭', title: 'Machine Capability Matrix: Tolerances, Software, Utilization', status: 'complete' },
              { num: '2', icon: '📦', title: 'Backlog to POs: Labor Hours by Program', status: 'complete' },
              { num: '3', icon: '📊', title: 'Quality: PPM Trends, Corrective Actions, Audit Results', status: 'complete' },
              { num: '4', icon: '✅', title: 'Compliance: OSHA, ITAR, Environmental with Expiration Tracking', status: 'progress' },
              { num: '5', icon: '💰', title: 'Pricing Adjustments, Surcharges, Raw Material Pass-Through', status: 'progress' },
              { num: '6', icon: '👥', title: 'Retention Plans for Supervisors, Programmers, Lead Machinists', status: 'pending' },
              { num: '7', icon: '📐', title: 'Layout Efficiency, Capital Projects, Automation Roadmap', status: 'pending' },
              { num: '8', icon: '📋', title: 'Supplier Agreements, Dual-Source Strategies, Long-Lead Inventory', status: 'pending' }
            ].map((item) => (
              <div
                key={item.num}
                className={`relative bg-white rounded-2xl border-2 p-6 transition-all duration-300 hover:-translate-y-1 ${
                  item.status === 'complete' ? 'border-green-300 bg-green-50/50' : 
                  item.status === 'progress' ? 'border-orange-300 bg-orange-50/50' : 
                  'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    item.status === 'complete' ? 'bg-green-500 text-white' : 
                    item.status === 'progress' ? 'bg-orange-500 text-white' : 
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {item.num}
                  </span>
                  <span className="text-3xl">{item.icon}</span>
                </div>
                <h3 className="text-xs font-semibold text-gray-900 leading-tight">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Manufacturing M&amp;A FAQs</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We field the same questions from machining owners nationwide. Here are the answers we
              provide as we architect your exit strategy.
            </p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="card-premium p-6 md:p-8 transition-all duration-300 hover:-translate-y-1"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Link */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Related Playbooks
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Explore adjacent AMIVA industrial coverage
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Many precision operators build embedded software, service divisions, or international
              demand. These playbooks extend the same rigor to connected growth paths.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPlaybooks.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1 hover:border-amiva-primary/40"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  Playbook
                </span>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-amiva-primary">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{item.description}</p>
                <span className="text-sm font-semibold text-amiva-primary group-hover:text-amiva-accent inline-flex items-center gap-2">
                  View details
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 10.5L10.5 3.5M10.5 3.5H4.375M10.5 3.5V9.625"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-amiva-primary to-amiva-accent text-white py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">Plan your machining exit with confidence.</h2>
            <p className="text-lg text-white/90">
              Benchmark your valuation, align on timing, and access the buyers who value your
              operation the way you do.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/valuation" className="bg-white text-amiva-primary font-semibold px-6 py-3 rounded-xl text-center">
              Start the Valuation
            </Link>
            <a
              href={appConfig.links.calendly}
              target="_blank"
              rel="noreferrer"
              className="border border-white/70 rounded-xl px-6 py-3 font-semibold text-center hover:bg-white/10 transition"
            >
              Book a Strategy Call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
